
import { useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { scaleIn, stagger } from "@/lib/animations";
import AddToCartButton from "@/components/shop/AddToCartButton";
import ShopCategoryFilter from "@/components/shop/ShopCategoryFilter";

type ShopProduct = {
  id: string;
  slug: string;
  name: string;
  brand: string;
  description: string;
  featured: boolean;
  price: number;
  inventory: number;
  imageUrl: string | null;
  categoryId: string;
};

type Category = {
  id: string;
  name: string;
  slug: string;
};

function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export default function ShopGrid({
  products,
  categories,
}: {
  products: ShopProduct[];
  categories: Category[];
}) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = activeCategory
    ? products.filter((p) => p.categoryId === activeCategory)
    : products;

  if (products.length === 0) {
    return (
      <div className="text-center py-16">
        <p
          className="text-2xl mb-3"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            color: colors.navy,
          }}
        >
          Shop Coming Soon
        </p>
        <p
          className="text-base"
          style={{
            fontFamily: fonts.body,
            fontWeight: 500,
            color: `${colors.navy}88`,
          }}
        >
          We&apos;re setting up direct shop products. Check back soon.
        </p>
      </div>
    );
  }

  return (
    <>
      <ShopCategoryFilter
        categories={categories.filter(category => products.some(product => product.categoryId === category.id))}
        onFilter={setActiveCategory}
      />

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <p
            className="text-base"
            style={{
              fontFamily: fonts.body,
              fontWeight: 600,
              color: `${colors.navy}88`,
            }}
          >
            No products in this category yet.
          </p>
        </div>
      ) : (
        <motion.div
          key={activeCategory ?? "all"}
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filtered.map((product, i) => (
            <motion.article
              key={product.id}
              variants={scaleIn}
              custom={i}
              className="rounded-2xl overflow-hidden border hover:shadow-lg transition-all duration-200"
              style={{
                backgroundColor: "white",
                borderColor: `${colors.tan}25`,
              }}
            >
              <Link to={`/shop/${product.slug}`}>
                <div
                  className="h-44 border-b"
                  style={{ borderColor: `${colors.tan}20` }}
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-full h-full"
                      style={{ backgroundColor: `${colors.tan}20` }}
                    />
                  )}
                </div>
              </Link>
              <div className="p-5">
                <p
                  className="text-[10px] tracking-[0.15em] uppercase mb-1"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: `${colors.navy}66`,
                  }}
                >
                  {product.brand}
                </p>
                <h2
                  className="text-lg mb-2"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    color: colors.navy,
                  }}
                >
                  <Link
                    to={`/shop/${product.slug}`}
                    className="hover:underline"
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    {product.name}
                  </Link>
                </h2>
                <p
                  className="text-sm mb-4 line-clamp-3"
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 500,
                    color: `${colors.navy}88`,
                  }}
                >
                  {product.description}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-base"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      color: colors.navy,
                    }}
                  >
                    {formatPrice(product.price)}
                  </span>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/shop/${product.slug}`}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-full text-xs border"
                      style={{
                        fontFamily: fonts.button,
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: colors.navy,
                        borderColor: `${colors.tan}40`,
                      }}
                    >
                      View
                    </Link>
                    <AddToCartButton
                      compact
                      product={{
                        productId: product.id,
                        slug: product.slug,
                        name: product.name,
                        brand: product.brand,
                        price: product.price,
                        imageUrl: product.imageUrl,
                      }}
                      outOfStock={product.inventory <= 0}
                    />
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      )}
    </>
  );
}
